"use client";
import { fontWeights } from "@/utils/fonts";
import Image from "next/image";

interface NavLink {
  name: string;
  href: string;
  show?: boolean;
}

interface HeaderData {
  logo?: string;
  navLinks?: NavLink[];
  letsTalk?: { name: string; href: string; show?: boolean };
  tickerMessages?: Array<{ text: string; show?: boolean }>;
  tickerBackgroundColor?: string;
  tickerTextColor?: string;
}

interface FooterData {
  logo?: string;
  description?: string;
  socialLinks?: Array<{ platform: string; url: string; icon?: string }>;
  services?: Array<{ name: string; href: string; show?: boolean }>;
  locations?: Array<{ address: string }>;
  email?: string;
  emailIcon?: string;
  locationIcon?: string;
  servicesTitle?: string;
  linksTitle?: string;
  contactTitle?: string;
  backgroundColor?: string;
  textColor?: string;
  hoverColor?: string;
}

export default function Footer({
  initialNavStatus,
  headerData,
  footerData,
}: {
  initialNavStatus?: Record<string, boolean>;
  headerData?: HeaderData;
  footerData?: FooterData;
}) {
  const navStatus = initialNavStatus || {};
  const cmsLogo = headerData?.logo;

  // Build the "Useful Links" list from CMS navLinks or fall back to navStatus filtering
  const cmsNavLinks = headerData?.navLinks?.filter(l => l.show !== false) ?? null;

  const isVisible = (href: string) => {
    if (href === '/') return true;
    const slug = href.replace(/^\//, '').toLowerCase();
    return navStatus[slug] !== false;
  };

  return (
    <footer
      className="bg-[#002F6D] text-white flex items-center justify-center mx-auto 
    h-full 
    sm:h-[250px] 
    md:h-[290px] 
    lg:h-[350px] 
    xl:h-[400px] 
    2xl:h-[450px] 
    w-full
    "
      style={{
        backgroundColor: footerData?.backgroundColor || '#002F6D',
        color: footerData?.textColor || '#ffffff'
      }}
    >
      <main
        className="w-full mx-auto grid grid-cols-1 sm:grid-cols-11 px-4
       max-w-[400px] 
       sm:max-w-[620px] 
       md:max-w-[760px] 
       lg:max-w-[900px] 
       xl:max-w-[1100px] 
       2xl:max-w-[1233px] 
       "
      >
        {/* Company Info */}
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left col-span-6 sm:col-span-4 mt-4 sm:mt-0">
          <Image
            src={cmsLogo || "/bitsshadow-logo.svg"}
            alt="Bitsshadow Logo"
            width={221}
            height={73}
            className="mx-auto sm:mx-0
            w-[100px] 
            lg:w-[151px] 
            xl:w-[182px] 
            2xl:w-[221px] 
            h-[35px] 
            lg:h-[51px] 
            xl:h-[60px] 
            2xl:h-[73px]
            "
          />
          <p
            className="leading-normal xl:leading-6 
          mt-1 
          sm:mt-2 
          lg:mt-3 
          xl:mt-4 
          text-[12px] 
          sm:text-[10px] 
          lg:text-[12px] 
          xl:text-[14px] 
          2xl:text-[16px]
          w-auto 
          tsm-350:w-[300px] 
          sm:w-[200px] 
          md:w-[230px] 
          lg:w-[270px]  
          xl:w-[315px] 
          2xl:w-[360px]
          "
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.regular }}
          >
            {footerData?.description || "Thank you for visiting BitsShadow's website. We warmly invite you to explore our blogs, where you may find valuable insights to inspire and guide you."}
          </p>
          {/* Social Media Icons */}
          <div className="flex justify-center sm:justify-start space-x-4 mt-1.5  sm:mt-2 lg:mt-3 xl:mt-4">
            {footerData?.socialLinks && footerData.socialLinks.length > 0 && (
              footerData.socialLinks.map((link, idx) => (
                <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer">
                  <Image
                    src={link.icon || (link.platform.toLowerCase().includes('linkedin') ? "/footer/linkedin.png" : "/footer/instagram.png")}
                    alt={`${link.platform} Icon`}
                    width={31}
                    height={31}
                    className="
                    w-[22px] 
                    sm:w-[22px] 
                    lg:w-[28px] 
                    xl:w-[35px] 
                    h-[22px] 
                    sm:h-[22px] 
                    lg:h-[28px] 
                    xl:h-[35px]
                    "
                  />
                </a>
              ))
            )}
          </div>
        </div>

        {/* Useful Links */}
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left col-span-5 sm:col-span-2 mt-4 sm:mt-0">
          <h2 className="
             text-[13px] 
             sm:text-[12px] 
             lg:text-[14px] 
             xl:text-[16px] 
             2xl:text-[20px] 
             mb-2 
             sm:mb-4 
             lg:mb-6 
             2xl:mb-8
             "
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}>
            {footerData?.linksTitle || "Useful Links"}
          </h2>
          <ul className="
          space-y-0.5
          sm:space-y-2 
          lg:space-y-3 
          xl:space-y-4 
          text-[11px] 
          sm:text-[10px] 
          lg:text-[12px] 
          xl:text-[14px] 
          2xl:text-[16px]
          "
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium }}>
            {cmsNavLinks
              ? cmsNavLinks
                .filter(link => isVisible(link.href))
                .map(link => (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      style={{ color: 'inherit' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                    >
                      {link.name}
                    </a>
                  </li>
                ))
              : (
                <>
                  {isVisible('/') && <li><a href="/" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>Home</a></li>}
                  {isVisible('/about') && <li><a href="/about" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>About</a></li>}
                  {isVisible('/services') && <li><a href="/services" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>Services</a></li>}
                  {isVisible('/portfolio') && <li><a href="/portfolio" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>Portfolio</a></li>}
                  {isVisible('/staff-augmentation') && <li><a href="/staff-augmentation" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>Staff Augmentation</a></li>}
                </>
              )
            }
          </ul>
        </div>

        {/* Our Services */}
        {isVisible('/services') && (
          <div className="flex flex-col items-center text-center sm:items-start sm:text-left col-span-6 sm:col-span-2 
            text-[8px] 
            sm:text-[12px] 
            lg:text-[14px] 
            xl:text-[16px] 
            2xl:text-[20px]
             mt-4 
            sm:mt-0
            ">
            <h2 className="text-[13px] sm:text-[12px] lg:text-[14px] xl:text-[16px] 2xl:text-[20px] mb-2 sm:mb-4 lg:mb-6 2xl:mb-8" style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}>
              {footerData?.servicesTitle || "Our Services"}
            </h2>
            <ul className="space-y-1 sm:space-y-2 lg:space-y-3 xl:space-y-4 text-[11px] sm:text-[10px] lg:text-[12px] xl:text-[14px] 2xl:text-[16px]" style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium }}>
              {footerData?.services && footerData.services.length > 0 ? (
                footerData.services.filter(s => s.show !== false).map((service, idx) => (
                  <li key={idx}>
                    <a
                      href={service.href}
                      style={{ color: 'inherit' }}
                      onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'}
                      onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
                    >
                      {service.name}
                    </a>
                  </li>
                ))
              ) : (
                <>
                  {isVisible('/web-development') && (
                    <li>
                      <a href="/web-development" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                        Web Development
                      </a>
                    </li>
                  )}
                  {isVisible('/app-development') && (
                    <li>
                      <a href="/app-development" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                        App Development
                      </a>
                    </li>
                  )}
                  {isVisible('/node-js') && (
                    <li>
                      <a href="/node-js" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                        Node.js
                      </a>
                    </li>
                  )}
                  {isVisible('/ui-ux-design') && (
                    <li>
                      <a href="/ui-ux-design" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                        UI/UX Design
                      </a>
                    </li>
                  )}
                  {isVisible('/react-native') && (
                    <li>
                      <a href="/react-native" style={{ color: 'inherit' }} onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'} onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}>
                        React Native
                      </a>
                    </li>
                  )}
                </>
              )}
            </ul>
          </div>
        )}

        {/* Get In Touch */}
        <div className="flex flex-col items-center text-center sm:items-start sm:text-left col-span-5 sm:col-span-2 mt-4 sm:mt-0">
          <h2 className="text-[13px] sm:text-[12px] lg:text-[14px] xl:text-[16px] 2xl:text-[20px] mb-2 sm:mb-4 lg:mb-6 2xl:mb-8" style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.semiBold }}>
            {footerData?.contactTitle || "Get In Touch"}
          </h2>
          <div className="
          flex flex-col items-center text-center sm:items-start sm:text-left
          space-y-2 
          lg:space-y-3 
          2xl:space-y-4 
          text-[11px]
          sm:text-[10px] 
          lg:text-[12px] 
          xl:text-[14px] 
          2xl:text-[16px] 
          w-auto
          tsm-350:w-[210px] 
          sm:w-[186px] 
          md:w-[195px] 
          lg:w-[236px] 
          xl:w-[280px] 
          2xl:w-[316px]
          "
            style={{ fontFamily: "Asap", fontWeight: fontWeights.asap.medium }}
          >
            {footerData?.locations && footerData.locations.length > 0 && footerData.locations.map((loc, idx) => (
              <div key={idx} className="flex flex-row items-start text-center sm:flex-row sm:items-start sm:text-left space-y-0 space-x-1 sm:space-x-2">
                <Image
                  src={footerData.locationIcon || "/footer/location.png"}
                  className="w-[7.5px] sm:w-[12px] lg:w-[15px] xl:w-[20px] h-[11px] sm:h-[17px] lg:h-[22px] xl:h-[29px] mt-1 sm:mt-0"
                  alt="Location Icon"
                  width={20}
                  height={20}
                />
                <p>{loc.address}</p>
              </div>
            ))}
            <div className="flex flex-row items-center text-center sm:text-left space-y-0 space-x-2 pb-2 sm:pb-0">
              <Image
                src={footerData?.emailIcon || "/footer/email.png"}
                className="w-[11px] sm:w-[15px] lg:w-[22.5px] xl:w-[24px] h-[8px] sm:h-[10px] lg:h-[16.5px] xl:h-[16px] sm:mt-0"
                alt="Email Icon"
                width={30}
                height={22}
              />
              <a
                href={`mailto:${footerData?.email || "contact@bitsshadow.com"}`}
                style={{ color: 'inherit' }}
                onMouseEnter={(e) => e.currentTarget.style.color = footerData?.hoverColor || '#FF9202'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'inherit'}
              >
                {footerData?.email || "contact@bitsshadow.com"}
              </a>
            </div>
          </div>
        </div>
      </main>
    </footer>
  );
}
